const COUNTY_SUFFIX = 'county';
const GEOLOCATION_PERMISSION_NAME = 'geolocation';
const STORAGE_STRING_MAX_LENGTH = 5000;
const DANGEROUS_STORAGE_CHARACTERS = new Set(['<', '>', '`']);

const sanitizeBrowserStorageString = (value) =>
  Array.from(value)
    .filter((character) => {
      const characterCode = character.codePointAt(0);
      return characterCode > 31 && characterCode !== 127 && !DANGEROUS_STORAGE_CHARACTERS.has(character);
    })
    .join('')
    .slice(0, STORAGE_STRING_MAX_LENGTH);

export const sanitizeBrowserStorageValue = (value) => {
  if (typeof value === 'string') {
    return sanitizeBrowserStorageString(value);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'boolean' || value === null || value === undefined) {
    return value ?? null;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeBrowserStorageValue(item));
  }

  if (typeof value === 'object') {
    return Object.entries(value).reduce((safeValue, [key, nestedValue]) => {
      const safeKey = sanitizeBrowserStorageString(key);

      if (!safeKey || safeKey === '__proto__' || safeKey === 'constructor' || safeKey === 'prototype') {
        return safeValue;
      }

      return {
        ...safeValue,
        [safeKey]: sanitizeBrowserStorageValue(nestedValue),
      };
    }, {});
  }

  return null;
};

export const returnDate = () => {
  let today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
  const yyyy = today.getFullYear();

  today = `${dd}/${mm}/${yyyy}`;
  return today;
};

export const reverseGeocode = async (latitude, longitude) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in reverse geocoding:', error);
    throw error;
  }
};

export const normalizeCountyName = (county) => {
  if (typeof county !== 'string') {
    return null;
  }

  const trimmedCounty = county.trim();
  const suffixStartIndex = trimmedCounty.length - COUNTY_SUFFIX.length;
  const suffixCandidate = trimmedCounty.slice(suffixStartIndex).toLowerCase();
  const characterBeforeSuffix = trimmedCounty[suffixStartIndex - 1];

  if (suffixCandidate === COUNTY_SUFFIX && characterBeforeSuffix?.trim() === '') {
    return trimmedCounty.slice(0, suffixStartIndex).trimEnd() || null;
  }

  return trimmedCounty || null;
};

const LOCATION_FIELD_MAX_LENGTH = 100;
const LOCATION_FIELD_WHITELIST = /^[\p{L}\p{M}\s.'-]{1,100}$/u;
const LATITUDE_RANGE = { min: -90, max: 90 };
const LONGITUDE_RANGE = { min: -180, max: 180 };

const isSafeLocationField = (value) => {
  if (typeof value !== 'string') {
    return false;
  }

  const trimmedValue = value.trim();
  return (
    trimmedValue.length > 0 &&
    trimmedValue.length <= LOCATION_FIELD_MAX_LENGTH &&
    LOCATION_FIELD_WHITELIST.test(trimmedValue)
  );
};

const sanitizeCoordinate = (value, { min, max }) => {
  const coordinate = Number(value);

  if (!Number.isFinite(coordinate) || coordinate < min || coordinate > max) {
    return null;
  }

  return coordinate;
};

export const sanitizeUserLocationCoordinates = (coordinates) => {
  const latitude = sanitizeCoordinate(coordinates?.latitude, LATITUDE_RANGE);
  const longitude = sanitizeCoordinate(coordinates?.longitude, LONGITUDE_RANGE);

  if (latitude === null || longitude === null) {
    return null;
  }

  return { latitude, longitude };
};

export const getUserLocation = () => {
  try {
    const userLocation = document.cookie
      .split('; ')
      .find((row) => row.startsWith('userLocation='))
      ?.split('=')[1];

    if (!userLocation) {
      return null;
    }

    return sanitizeUserLocationCoordinates(JSON.parse(decodeURIComponent(userLocation)));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing user location:', error);
    return null;
  }
};

export const storeResolvedLocationData = (data) => {
  const rawCountry = typeof data?.address?.country === 'string' ? data.address.country : null;
  const rawCounty = normalizeCountyName(data?.address?.state || data?.address?.county);
  const country = rawCountry?.trim() || null;
  const county = rawCounty?.trim() || null;
  const safeCountry = isSafeLocationField(country) ? country : null;
  const safeCounty = isSafeLocationField(county) ? county : null;

  if (safeCountry) {
    localStorage.setItem('country', safeCountry);
  }

  if (safeCounty) {
    localStorage.setItem('county', safeCounty);
  }

  return {
    country: safeCountry,
    county: safeCounty,
  };
};

export const requestCurrentLocation = () =>
  new Promise((resolve, reject) => {
    // Required after browser consent to resolve county-specific emergency contacts and local guidance.
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true }); // NOSONAR
  });

export const getGeolocationPermissionState = async () => {
  if (!navigator.geolocation) {
    return 'unsupported';
  }

  if (!navigator.permissions?.query) {
    return 'unknown';
  }

  try {
    // Required to avoid triggering a browser prompt when geolocation is already blocked.
    const permissionStatus = await navigator.permissions.query({ name: GEOLOCATION_PERMISSION_NAME }); // NOSONAR
    return permissionStatus.state || 'unknown';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error checking geolocation permission:', error);
    return 'unknown';
  }
};

export const persistUserLocation = async (position) => {
  const coordinates = sanitizeUserLocationCoordinates({
    latitude: position?.coords?.latitude,
    longitude: position?.coords?.longitude,
  });

  if (!coordinates) {
    throw new Error('Invalid geolocation coordinates');
  }

  const encodedCoordinates = encodeURIComponent(JSON.stringify(coordinates));
  document.cookie = `userLocation=${encodedCoordinates}; max-age=${60 * 60 * 24}; path=/; SameSite=Lax`;

  try {
    const data = await reverseGeocode(coordinates.latitude, coordinates.longitude);
    const resolvedLocation = storeResolvedLocationData(data);

    return {
      coordinates,
      data,
      ...resolvedLocation,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error storing reverse geocode data:', error);

    return {
      coordinates,
      data: null,
      country: null,
      county: null,
    };
  }
};
